<template>
  <d-modal
    class="modal-big"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('customer.contact.editTitle') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <div v-if="!allDataLoaded || saveInProgress" class="pb-3 pt-3 text-center popup-overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>
      <d-tabs class="tabs-in-modal">
        <d-tab
          :title="$t('ui.generalInfo')"
          active>
          <h4 class="mt-3 mb-3 pb-3 border-bottom d-flex">
            {{ $t('ui.generalInfo') }}

            <div
              v-if="communication && communication.id"
              class="ml-auto">
              <small>
                {{ $t('inquiries.inquiryStatus') }}:
                <strong
                  :class="{
                    'text-success': communication.customer_status === 'ACCEPTED',
                    'text-danger': communication.customer_status === 'REJECTED'
                  }">
                  <template v-if="communication.customer_status">
                    {{ $t(`complaints.statusNames.${communication.customer_status.toLowerCase()}`) }}
                  </template>
                  <template v-else>
                    {{ $t('ui.lack') }}
                  </template>
                </strong>
              </small>
            </div>
          </h4>

          <d-form>
            <validation-observer ref="contact-edit-form" tag="form">
              <div class="form-group">
                <label>
                  {{ $t('customer.contact.contactDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :disabled-dates="futureDates"
                    :disabled="!can_edit"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="customer_id"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToContact') }}:
                </label>

                <d-button
                  v-if="!contactIsEditable && customer_contact && can_edit"
                  theme="primary"
                  size="sm"
                  class="ml-2"
                  @click="contactIsEditable = true">
                  <i class="material-icons">edit</i>
                  {{ $t('ui.edit') }}
                </d-button>

                <contact-customer-contact
                  v-show="contactIsEditable || !customer_contact"
                  ref="contactSelect"
                  :customerId="customer_id"
                  :readOnly="!can_edit" />

                <div
                  v-if="!contactIsEditable && customer_contact"
                  class="mt-3 mb-4">
                  {{ customer_contact.name }}
                  <br v-if="customer_contact.name">
                  {{ customer_contact.email }}
                  <br v-if="customer_contact.email">
                  {{ customer_contact.phone }}
                </div>
              </div>

              <div class="form-group">
                <label class="d-block">
                  {{ $t('customer.contact.contactType') }}:
                </label>

                <d-form-checkbox
                  v-for="(contactType, index) of contactTypes"
                  :key="'contact-type-checkbox-' + index"
                  inline
                  v-model="types"
                  :value="contactType.type"
                  :disabled="!can_edit">
                  {{ contactType.name }}
                </d-form-checkbox>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('customer.contact.descriptionOptional') }}:
                </label>

                <d-textarea
                  :disabled="!can_edit"
                  :value.prop="description"
                  v-model="description" />
              </div>

              <div
                v-if="communication && communication.id"
                class="form-group">
                <label for="customer-descirption">
                  {{ $t('customer.contact.customerResponse') }}:
                </label>

                <template v-if="communication.customer_answer_at">
                  <div class="py-2">
                    {{ communication.customer_description }}
                  </div>
                  <small class="form-text text-muted">
                    {{ $t('customer.contact.repliedOn') }} {{ dateAsString(communication.customer_answer_at) }}
                  </small>
                </template>
                <template v-else>
                  <div>{{ $t('customer.contact.noResponse') }}</div>
                </template>
              </div>
            </validation-observer>
          </d-form>
        </d-tab>
        <d-tab
         :title="$t('customer.contact.comments')">
          <contact-comments
            v-if="!loadError && allDataLoaded"
            :class="'mt-3'"
            :comments="comments"
            :contact-id="contactID" />
        </d-tab>
        <d-tab
          :title="$t('customer.contact.notifications')">
          <contact-notifications
            v-if="!loadError && allDataLoaded"
            class="mt-3"
            :notifications="notifications"
            :contactID="contactID" />
        </d-tab>
      </d-tabs>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        :disabled="saveInProgress || !can_edit"
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        <div
          v-if="saveInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        <template v-if="!saveInProgress">
          {{ $t('ui.saveChanges') }}
        </template>
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import ContactCustomerContact from './ContactCustomerContact.vue';
import ContactNotifications from './ContactNotifications.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import getPrefferedLanguage from './../../utils/i18nUtils.js';
import Vue from 'vue';
import ContactComments from '@/components/contacts/ContactComments.vue';

export default {
  name: 'contact-popup',
  components: {
    'contact-customer-contact': ContactCustomerContact,
    'contact-comments': ContactComments,
    'contact-notifications': ContactNotifications
  },
  props: [
    'contactID'
  ],
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedTypes;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    futureDates () {
      return {
        from: new Date()
      };
    }
  },
  data() {
    return {
      customer_id: null,
      customer_contact_id: null,
      loadedData: false,
      loadError: false,
      date: new Date(),
      types: ['PHONE'],
      description: '',
      can_edit: false,
      communication: {},
      comments: [],
      notifications: null,
      customer_contact: {},
      saveInProgress: false,
      contactIsEditable: false,
      loadedTypes: false,
      contactTypes: []
    };
  },
  mounted () {
    this.loadItemData();
    this.loadAdditionalData();
    this.$bus.$on('comments-reload-needed', this.reloadCommentsView);
    this.$bus.$on('contact-notifications-reload-needed', this.reloadNotificationsView);
    this.$bus.$on('ui-select-customer-contact-updated', this.updateCustomerContact);
  },
  methods: {
    reloadCommentsView () {
      this.loadedData = false;

      FormUtils.loadItemData(this, {
        endpointBase: '/contacts/item/',
        overrideID: this.contactID,
        instanceFields: ['comments'],
        loadedKey: 'loadedData',
        errorKey: 'loadError'
      });
    },
    reloadNotificationsView () {
      this.loadedData = false;

      FormUtils.loadItemData(this, {
        endpointBase: '/contacts/item/',
        overrideID: this.contactID,
        instanceFields: ['notifications'],
        loadedKey: 'loadedData',
        errorKey: 'loadError'
      });
    },
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/contacts/item/',
        overrideID: this.contactID,
        instanceFields: ['types', 'customer_id', 'customer_contact_id', 'customer_contact', 'description', 'comments', 'notifications', 'can_edit', 'communication'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        successAction: (response) => {
          if (response) {
            let dateString = response.date;
            Vue.set(this, 'date', new Date(dateString));

            Vue.nextTick(() => {
              if (this.customer_contact_id) {
                if (this.$refs['contactSelect']) {
                  this.$refs['contactSelect'].setContactID(this.customer_contact_id);
                }
              }
            });
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-contact-types/items',
        loadedKey: 'loadedTypes',
        outputKey: 'contactTypes',
        errorKey: 'loadError'
      });
    },
    handleClose () {
      this.$bus.$emit('close-order-contact-edit-popup');
    },
    validateForm () {
      FormUtils.validate(this.$refs['contact-edit-form'], this.submitForm);
    },
    submitForm () {
      this.saveInProgress = true;

      let formDataToSend = {
        date: [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        types: this.types,
        description: this.description
      };

      if (this.customer_contact_id) {
        formDataToSend.customer_contact_id = this.customer_contact_id;
      } else {
        formDataToSend.customer_contact_id = null;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/contacts/update/' + this.contactID,
        formData: formDataToSend,
        successTitle: this.$t('customer.contact.saveContactSuccessTitle'),
        successText: this.$t('customer.contact.saveContactSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.contact.saveContactErrorText'),
        successAction: () => {
          this.saveInProgress = false;
          this.$bus.$emit('contacts-refresh-list');
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    },
    updateCustomerContact (newID) {
      this.customer_contact_id = newID;
    },
    dateAsString (dateString) {
      if (!dateString) {
        return '';
      }

      let date = new Date(dateString);

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  beforeDestroy () {
    this.$bus.$off('comments-reload-needed', this.reloadCommentsView);
    this.$bus.$off('contact-notifications-reload-needed', this.reloadNotificationsView);
    this.$bus.$off('ui-select-customer-contact-updated', this.updateCustomerContact);
  }
};
</script>

<style lang="scss">
.tabs-in-modal .nav-tabs .nav-link {
  background-color: #F5F6F8;

  &.active {
    background-color: #FFF;
  }
}
</style>
